import React, { useMemo, useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { extractLocation } from '../utils/locationCoords';
import 'leaflet/dist/leaflet.css';
import './TransactionMap.css';

// Component to render circles directly with Leaflet
function LocationCircles({ locations, formatCurrency }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    // Remove existing layer
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }

    // Create new layer group
    const layerGroup = L.layerGroup();

    // Calculate max count for scaling
    const maxCount = Math.max(...locations.map(l => l.count), 1);

    locations.forEach(location => {
      // Scale radius based on count (pixels)
      const minRadius = 8;
      const maxRadius = 35;
      const radius = minRadius + (location.count / maxCount) * (maxRadius - minRadius);

      const circle = L.circleMarker([location.lat, location.lng], {
        radius: radius,
        color: '#ff3b30',
        fillColor: '#ff3b30',
        fillOpacity: 0.5,
        weight: 2
      });

      // Create popup content
      const popupContent = `
        <div class="map-popup">
          <strong>${location.name}</strong>
          <div class="popup-stats">
            <span>${location.count} transactions</span>
            <span>${formatCurrency(location.total)}</span>
          </div>
          <div class="popup-transactions">
            ${location.transactions.map(t => `
              <div class="popup-tx">
                <span class="popup-tx-desc">${t.description || 'N/A'}</span>
                <span class="popup-tx-amount ${t.amount >= 0 ? 'income' : 'expense'}">
                  ${formatCurrency(t.amount)}
                </span>
              </div>
            `).join('')}
            ${location.count > 5 ? `<div class="popup-more">+${location.count - 5} more</div>` : ''}
          </div>
        </div>
      `;

      circle.bindPopup(popupContent);
      layerGroup.addLayer(circle);
    });

    layerGroup.addTo(map);
    layerRef.current = layerGroup;

    // Fit bounds to show all markers
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(l => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
    }

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [map, locations, formatCurrency]);

  return null;
}

function TransactionMap({ transactions }) {
  // Aggregate transactions by location
  const locationData = useMemo(() => {
    const byLocation = {};

    if (!transactions || transactions.length === 0) {
      return [];
    }

    transactions.forEach(t => {
      const location = extractLocation(t.Description);
      if (!location) return;

      const key = `${location.lat},${location.lng}`;
      if (!byLocation[key]) {
        byLocation[key] = {
          ...location,
          count: 0,
          total: 0,
          transactions: []
        };
      }

      byLocation[key].count++;
      const amount = typeof t.Amount === 'number' ? t.Amount :
        parseFloat(String(t.Amount).replace(/[$,]/g, '')) || 0;
      byLocation[key].total += Math.abs(amount);
      if (byLocation[key].transactions.length < 5) {
        byLocation[key].transactions.push({
          description: t.Description?.substring(0, 40),
          amount: amount,
          date: t.Date
        });
      }
    });

    return Object.values(byLocation);
  }, [transactions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Default center (US)
  const defaultCenter = [39.8283, -98.5795];

  if (locationData.length === 0) {
    return (
      <div className="transaction-map">
        <h3>Transaction Locations</h3>
        <div className="map-empty">
          No location data found in transactions
          <br />
          <small>({transactions?.length || 0} transactions checked)</small>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-map">
      <h3>Transaction Locations</h3>
      <div className="map-container">
        <MapContainer
          center={defaultCenter}
          zoom={4}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationCircles locations={locationData} formatCurrency={formatCurrency} />
        </MapContainer>
      </div>
      <div className="map-stats">
        <span>{locationData.length} locations</span>
        <span>{locationData.reduce((sum, l) => sum + l.count, 0)} mapped transactions</span>
        <span>{formatCurrency(locationData.reduce((sum, l) => sum + l.total, 0))} total</span>
      </div>
    </div>
  );
}

export default TransactionMap;
